import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from "../contexts/auth";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getReportAttendance, getReportAttendanceOU } from '../services/api_reports';
import { exportAttendanceReports, exportAttendanceAnalyticReports } from '../services/api_extractions';
import ListBoxPageSize from './listbox_pase_size';
import Swal from 'sweetalert2';
import { ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { estraiMessaggiErrore } from '../services/utils';
import CustomTooltipComponent from './custom_tooltip_component';
import PrettyPagination from './pretty_pagination';



function Row({ token, row, justificationCodes, startDate, endDate, controller }) {
  const [open, setOpen] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState(false);

  const fetchEmployeeDetails = async () => {
    setLoadingDetails(true);
    setErrorDetails(false);
    let ouId = row?.ou?.id;

    if(controller.current) {
      controller.current.abort();
      }
      controller.current = new AbortController();
      const signal = controller.current.signal;

    try {
      const details = await getReportAttendanceOU(
        token,
        { startDate, endDate },
        null,
        1,
        25,
        ouId,
        signal,
        'attendance'
      );
      setEmployeeDetails(details.results);
    } catch (err) {
      if (err?.name != 'CanceledError') {
        console.error('Error fetching employee details:', err);
        setErrorDetails(true);
      }
    } finally {
        setLoadingDetails(false);
    }
  };


  const getJustificationDuration = (code, justification_requests) => {
    const justification = justification_requests.find((request) => request.code === code);
    
    if (!justification) return '';

    if (justification.duration === null) return '';

    const formattedDuration = parseFloat(justification.duration.toFixed(2));

    if (justification.strategy === 'decrease') {
      return `-${formattedDuration}`;
    }

    if (justification.strategy === 'increase') {
      return `+${formattedDuration}`;
    }

    if (justification.strategy === 'null') {
      return <span className='text-gray-400'>"{formattedDuration}"</span>;
    }
  
    return '';
  };

  const getJustificationDescription = (code) => {
  
    const justification = row.justification_requests.find((request) => request.code === code);
    return justification ? justification.description : '';
  };

  return (
    <>
      <TableRow sx={{cursor: 'pointer', '& > *': { borderBottom: 'unset' }, "&:hover": { backgroundColor: "rgba(99, 102, 241, 0.04)" }, }}
        onClick={() => {setOpen(!open); if (!open) {fetchEmployeeDetails();}}}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {setOpen(!open); if (!open) {fetchEmployeeDetails();}}}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.ou.name}
        </TableCell>
        <TableCell align="right">{row.shift_total}</TableCell>
        <TableCell align="right">{row.shift_net_decrease_justification}</TableCell>
        {justificationCodes?.map((code) => (
          <TableCell key={code} align="right">
            <CustomTooltipComponent title={getJustificationDescription(code)}>
              <span tabIndex={0}>{getJustificationDuration(code, row.justification_requests)}</span>
            </CustomTooltipComponent>
          </TableCell>
        ))}
        <TableCell align="right">{row.shift_net}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6 + justificationCodes.length} className='bg-indigo-50'>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="p" gutterBottom component="div" className='font-semibold text-indigo-800 uppercase'>
                Dettaglio Dipendenti
              </Typography>
              {loadingDetails ? (
                <CircularProgress />
              ) : errorDetails ? (
                <Alert severity="error">Errore nel caricamento dei dettagli</Alert>
              ) : (
                <Table size="small" aria-label="employee details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dipendente</TableCell>
                      <TableCell align="right">Ore Teoriche</TableCell>
                      <TableCell align="right">Ore Ordinarie</TableCell>
                      {justificationCodes?.map((code) => (
                        <TableCell key={code} align="right">{code}</TableCell>
                      ))}
                      <TableCell align="right">Ore Effettive</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeDetails.length > 0 ? (
                      employeeDetails?.map((detail) => (
                        <TableRow key={detail.employee.id} className={`${detail.is_ou_member ? '' : 'bg-gray-200'}`}>
                          <TableCell>
                            {detail.employee.last_name && detail.employee.first_name 
                              ? `${detail.employee.last_name} ${detail.employee.first_name}` 
                              : detail.employee.cf}
                          </TableCell>
                          <TableCell align="right">{detail.shift_total}</TableCell>
                          <TableCell align="right">{detail.shift_net_decrease_justification}</TableCell>
                          {justificationCodes?.map((code) => {
                            const justification_requests = detail.justification_requests;
                            
                            return (
                            <TableCell key={code} align="right">
                              <CustomTooltipComponent title={getJustificationDescription(code)}>
                                <span tabIndex={0}>{getJustificationDuration(code, justification_requests)}</span>
                              </CustomTooltipComponent>
                            </TableCell>
                          )})}
                          <TableCell align="right">{detail.shift_net}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6 + justificationCodes.length} align="center">
                          Nessun dettaglio disponibile
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    ou: PropTypes.shape({
      name: PropTypes.string
    }),
    attendance_expected: PropTypes.string,
    attendance_actual: PropTypes.string,
    justification_requests: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        name: PropTypes.string,
        duration: PropTypes.number,
        strategy: PropTypes.string
      })
    )
  }),
  justificationCodes: PropTypes.arrayOf(PropTypes.string),
};

export default function CollapsibleTable({ selectedUnitId, startDate, endDate }) {
  const { token } = useContext(AuthContext);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isDownloadError, setIsDownloadError] = useState(false);
  const [justificationCodes, setJustificationCodes] = useState([]);
  const [justificationDescriptions, setJustificationDescriptions] = useState({});
  const [totalResult, setTotalResult] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const controller = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const previousSelectedUnitId = useRef(selectedUnitId);

  useEffect(() => {
    setPage(1);
  }, [selectedUnitId]);


  useEffect(() => {
    if (previousSelectedUnitId.current !== selectedUnitId && page !== 1) {
      previousSelectedUnitId.current = selectedUnitId;
      setPage(1);
      return;
    }
    previousSelectedUnitId.current = selectedUnitId;

    const fetchUnits = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const selectedUnitParam = selectedUnitId !== null ? selectedUnitId : undefined;
        if(controller.current) {
          controller.current.abort();
          }
          controller.current = new AbortController();
          const signal = controller.current.signal;
    
        const response = await getReportAttendance(
          token,
          { startDate, endDate },
          selectedUnitParam,
          [],
          page,
          pageSize,
          signal,
          'attendance'
        );

        const totalItems = response.count || 0;
        setTotalResult(totalItems);

        const fetchedUnits = response.results;
        
        const allJustificationCodes = [
          ...new Set(fetchedUnits?.flatMap((unit) => unit?.justification_requests?.map((request) => request.code)))
        ];

        const descriptions = allJustificationCodes.reduce((acc, code) => {
          const justification = fetchedUnits
            .flatMap((unit) => unit?.justification_requests)
            .find((request) => request.code === code);
          acc[code] = justification ? justification.description : '';
          return acc;
        }, {});
        
        setUnits(fetchedUnits);
        setJustificationCodes(allJustificationCodes);
        setJustificationDescriptions(descriptions);
        setIsLoading(false);
      } catch (err) {
        if (err?.name != 'CanceledError') {
          console.error('Error fetching units:', err);
          setError(true);
          setIsLoading(false); 
        }
        
      }
    };

    fetchUnits();
  }, [startDate, endDate, token, page, pageSize, selectedUnitId]);


  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize); 
    setPage(1); 
  };

  const handleExportReports = async (selectedUnit, startDate, endDate) => {
    setIsLoading(true);
    setIsDownloadError(false);

    try {
      const unitParam = selectedUnit ?? null;
      const { status, data } = await exportAttendanceReports(token, startDate, endDate, unitParam);

      const alreadyExists =
        status === 200 && String(data?.detail || "").toLowerCase().includes("già");

      Swal.fire({
        icon: alreadyExists ? "warning" : "success",
        title: alreadyExists ? "Esportazione già in corso" : "Esportazione avviata",
        html: `
          <div>
            ${
              alreadyExists
                ? "Esiste già un export per gli stessi parametri."
                : "Il file verrà generato in background e sarà scaricabile quando pronto."
            }
            <br/>
            <a href="/download_list" style="color:#4f46e5; text-decoration:underline;">
              Clicca qui
            </a>
            per andare alla pagina download.
          </div>
        `,
        confirmButtonText: "Chiudi",
      });
    } catch (error) {
      setIsDownloadError(true);

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      if (status === 404) {
        Swal.fire({
          icon: "info",
          title: "Nessun dato trovato",
          text: detail || "Nessun dato per il periodo selezionato.",
          confirmButtonText: "OK",
        });
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: detail || estraiMessaggiErrore(error) || "Errore, riprova più tardi.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReportsAnalytic = async (selectedUnit, startDate, endDate) => {
    setIsLoading(true);
    setIsDownloadError(false);
    try {
      const unitParam = selectedUnit ?? null;
      const { status, data } = await exportAttendanceAnalyticReports(
        token,
        startDate,
        endDate,
        unitParam
      );

      const alreadyExists =
        status === 200 && String(data?.detail || "").toLowerCase().includes("già");
      Swal.fire({
        icon: alreadyExists ? "warning" : "success",
        title: alreadyExists ? "Esportazione già in corso" : "Esportazione avviata",
        html: `
          <div>
            ${
              alreadyExists
                ? "Esiste già un export analitico per gli stessi parametri."
                : "Il file analitico verrà generato in background e sarà scaricabile quando pronto."
            }
            <br/>
            <a href="/download_list" style="color:#4f46e5; text-decoration:underline;">
              Clicca qui
            </a>
            per andare alla pagina download.
          </div>
        `,
        confirmButtonText: "Chiudi",
      });
    } catch (error) {
      setIsDownloadError(true);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 404) {
        Swal.fire({
          icon: "info",
          title: "Nessun dato trovato",
          text: detail || "Nessun dato per il periodo selezionato.",
          confirmButtonText: "OK",
        });
        return;
      }
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: detail || estraiMessaggiErrore(error) || "Errore, riprova più tardi.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">Errore nel caricamento della sede</Alert>;
  }

  const totalPages = Math.ceil(totalResult / pageSize);

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 py-2 px-3 border-x border-b border-gray-200 rounded-b-lg bg-gray-50">
        <ListBoxPageSize value={pageSize} onChange={handlePageSizeChange} />
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowExportDropdown((prev) => !prev)}
            aria-expanded={showExportDropdown}
            className="inline-flex h-8 items-center gap-2 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
          >
            Seleziona Export<ChevronDownIcon className={`h-4 w-4 transition-transform ${showExportDropdown ? "rotate-180" : ""}`} />
          </button>
          {showExportDropdown && (
            <div className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  handleExportReports(selectedUnitId, startDate, endDate);
                  setShowExportDropdown(false);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
              >
                Sintetico
              </button>
              <button
                type="button"
                onClick={() => {
                  handleExportReportsAnalytic(selectedUnitId, startDate, endDate);
                  setShowExportDropdown(false);
                }}
                className="block w-full border-t border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
              >
                Analitico
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* <div className='mb-3 py-2 px-6 rounded-sm shadow-md border border-gray-100 cursor-pointer' onClick={() => setShowInfo(!showInfo)}>
        <InformationCircleIcon className="inline-block me-3 w-5 h-5 text-indigo-800" />
        <span className='text-sm uppercase font-semibold text-indigo-800'>{showInfo ? 'Chiudi istruzioni' : 'Mostra istruzioni'}</span>
        {showInfo && ( 
          <div className='text-justify text-sm mt-4 pb-2'>
            <p>
              Nelle sezioni della tabella principale è possibile visualizzare il totale delle ore associate a ciascuna unità organizzativa, in base al periodo selezionato.
              <br />Le ore non associate ad alcuna unità sono riportate nella riga "Senza Unità".
              <br />Le "Ore Teoriche" corrispondono al totale delle ore dei turni associate all'unità.
              <br />Le "Ore Ordinarie" rappresentano il totale delle ore teoriche al netto dei giustificativi negativi.
              <br />Le "Ore Effettive" sono calcolate sommando alle ore teoriche i giustificativi di incremento e sottraendo i giustificativi di decremento.
              <br />La somma di ogni giustificativo associato all'Unità viene identificata tramite il proprio codice e visualizzata con i simboli +/- in base all'azione che svolge sulle ore lavorative.
              <br />Selezionando il simbolo "⌄" accanto a ciascuna unità organizzativa, è possibile visualizzare il dettaglio delle ore e verificare come sono distribuite tra i dipendenti. Le ore assegnate a un'unità ma associate a dipendenti che non ne fanno parte saranno evidenziate con uno sfondo grigio. 
            </p>
          </div>
        )}
      </div> */}

      <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <button
          type="button"
          onClick={() => setShowInfo((prev) => !prev)}
          aria-expanded={showInfo}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-indigo-50/50 sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
              <InformationCircleIcon className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <p className="text-sm font-semibold uppercase text-indigo-800">
                Istruzioni di lettura
              </p>

              <p className="mt-0.5 text-xs text-gray-500">
                Consulta il significato dei dati mostrati nella tabella
              </p>
            </div>
          </div>

          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${showInfo ? "rotate-180" : ""}`}/>
        </button>

        {showInfo && (
          <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 sm:px-5">
            <div className="space-y-3 text-sm leading-6 text-gray-700">
              <p>
                La tabella mostra il totale delle ore associate a ciascuna unità organizzativa nel periodo selezionato.
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Ore teoriche
                  </p>

                  <p className="mt-1">
                    Totale delle ore previste dai turni associati all’unità.
                  </p>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Ore ordinarie
                  </p>

                  <p className="mt-1">
                    Ore teoriche al netto dei giustificativi con effetto negativo.
                  </p>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Ore effettive
                  </p>

                  <p className="mt-1">
                    Ore teoriche corrette applicando gli incrementi e i decrementi dei giustificativi.
                  </p>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Dettaglio dipendenti
                  </p>

                  <p className="mt-1">
                    Apri una riga per verificare la distribuzione delle ore tra i dipendenti.
                  </p>
                </div>
              </div>

              <p className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-indigo-800">
                Le ore non associate ad alcuna unità sono riportate nella riga
                <span className="font-semibold"> “Senza Unità”</span>. I dipendenti non appartenenti all’unità selezionata vengono evidenziati con uno sfondo grigio.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
        <TableContainer component={Paper} elevation={0}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Unità Operativa</TableCell>
                <TableCell align="right">Ore Teoriche</TableCell>
                <TableCell align="right">Ore Ordinarie</TableCell>
                {justificationCodes?.map((code) => (
                  <TableCell key={code} align="right" className='cursor-pointer'>
                    <CustomTooltipComponent title={justificationDescriptions[code]}>
                      <span>{code}</span>
                    </CustomTooltipComponent>
                  </TableCell>
                ))}
                <TableCell align="right">Ore Effettive</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {units?.map((row) => (
                <Row controller={controller} token={token} key={row.ou.id} row={row} justificationCodes={justificationCodes} startDate={startDate} endDate={endDate} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <PrettyPagination
        page={page}
        totalPages={totalPages}
        totalCount={totalResult}
        onChange={(newPage) => setPage(newPage)}
        labelSingolare="risultato"
        labelPlurale="risultati"
        className="rounded-b-xl border-x border-b border-gray-200 bg-white px-4 py-4 shadow-sm"
      />
    </>
  );
}
